import { useState, type ChangeEvent, type FC, type RefObject } from 'react'

import type { ComboboxRef } from '@skytech/ui'
import { Combobox, Input, LoaderCircle, Select } from '@skytech/ui'
import { DECIMAL_PATTERN, MAX_DECIMAL_12_4, MIN_PRICE, trimLeadingZeros } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Field } from 'formik'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'
import type { Customer } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import type { FormFields } from '../AssetDetails/types'

export const Hardware: FC<{
  isCustomerAdmin: boolean
  comboboxMethodsRef: RefObject<ComboboxRef>
}> = ({ isCustomerAdmin, comboboxMethodsRef }) => {
  const translate = useTranslation()
  const profile = useProfile()
  const [currency, setCurrency] = useState<string>(profile?.currency || '')

  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)
  const { data: customers, isLoading: isCustomersLoading } = useQuery(
    queries.adminCustomers(),
    adminResources.getCustomers
  )

  if (isCustomersLoading) {
    return (
      <div className="mx-2 grid grid-cols-2 gap-4 rounded-lg">
        <LoaderCircle />
      </div>
    )
  }

  const handleCostChange = (e: ChangeEvent<HTMLInputElement>, form: FormikProps<FormFields>) => {
    let newValue = e.target.value.trim()
    if (!DECIMAL_PATTERN.test(newValue) && newValue !== '') return
    form.setFieldValue('cost', newValue)
  }

  return (
    <div className="wrap flex flex-col md:flex-row">
      <div className="mx-2 flex flex-1 basis-0 flex-col rounded-lg md:basis-[30rem]">
        <Field name="typeId">
          {({ field, form }: FieldProps) => (
            <Select
              capitalizeLabel
              id="typeId"
              label={
                <>
                  <Translation id="Type" />*
                </>
              }
              options={
                assetTypes?.types.map(({ id, type }) => ({
                  value: id,
                  label: translate(`asset_type/${type}`),
                })) || []
              }
              marginBottom={16}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>

        <Field name="assetDescription">
          {({ field, meta }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="assetDescription"
              className="description-input"
              ariaLabel={translate('Description')}
              label={
                <>
                  <Translation id="Description" />*
                </>
              }
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
            />
          )}
        </Field>

        {!isCustomerAdmin && <ImeiSnrFields />}
      </div>

      <div className="mx-2 flex flex-1 basis-0 flex-col rounded-lg md:basis-[30rem]">
        {isCustomerAdmin && <ImeiSnrFields />}

        {!isCustomerAdmin && (
          <Field name="customerName">
            {({ field, meta, form }: FieldProps) => (
              <Combobox
                id="customerName"
                capitalizeLabel
                isFullWidth
                hitsTranslation={<Translation id="hit(s)" />}
                noResultsFoundTranslation={<Translation id="No results found" />}
                marginBottom={16}
                label={
                  <>
                    <Translation id="Company" />*
                  </>
                }
                ariaLabel={translate('Company')}
                list={customers || []}
                stringMatcher={(item: Customer | null) =>
                  item ? `${item.name} ${item.orgNo}` : ''
                }
                touched={meta.touched}
                error={meta.error}
                placeholder={translate('Choose company...')}
                onSetFieldTouched={() => form.setFieldTouched('customerName')}
                {...field}
                onSetFieldValue={(v: string) => {
                  form.setFieldValue('customerName', v)
                  const selectedCustomer = customers?.find(
                    customer => `${customer.name} ${customer.orgNo}` === v
                  )
                  setCurrency(selectedCustomer?.currency || profile?.currency || '')
                }}
                onCustomChange={() => {
                  form.setFieldValue('userName', '')
                  form.setFieldTouched('userName')
                  comboboxMethodsRef.current?.onResetCombobox()
                }}
              />
            )}
          </Field>
        )}

        <Field name="cost">
          {({ field, meta, form }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="cost"
              type="text"
              inputMode="decimal"
              min={String(MIN_PRICE)}
              max={String(MAX_DECIMAL_12_4)}
              LeftElement={<span>{currency}</span>}
              ariaLabel={translate('Cost')}
              label={
                <>
                  <Translation id="Cost" />*
                </>
              }
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
              value={trimLeadingZeros(field.value)}
              onChange={e => handleCostChange(e, form)}
            />
          )}
        </Field>
      </div>
    </div>
  )
}

const ImeiSnrFields: FC = () => {
  const translate = useTranslation()
  return (
    <div className="wrap items-between flex flex-col justify-between gap-0 md:flex-row md:gap-5">
      <div className="flex flex-1">
        <Field name="imeiNumber">
          {({ field, meta }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="imeiNumber"
              ariaLabel={translate('IMEI')}
              label={<Translation id="IMEI" />}
              wrapperClassName="w-full"
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
            />
          )}
        </Field>
      </div>
      <div className="flex flex-1">
        <Field name="serialNumber">
          {({ field, meta }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="serialNumber"
              ariaLabel={translate('Serialnbr')}
              label={<Translation id="Serialnbr" />}
              wrapperClassName="w-full"
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
            />
          )}
        </Field>
      </div>
    </div>
  )
}
